/** @module @liquidefy/code-editor/language/language-completion @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS — types and engine dependencies ═══

import type {
  CompletionItem,
  CompletionKind,
  CompletionContext,
  CompletionId,
  LanguageId,
  Position,
  Range,
  EditOperation,
  DocumentSymbol,
  SnippetDefinition,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asCompletionId,
  generateId,
  asLanguageId,
} from '../code-editor.js';

// ═══ §2 COMPLETION FILTERING — fuzzy, prefix, substring, camelCase matching ═══

interface FilterOptions {
  readonly caseSensitive?: boolean;
  readonly maxResults?: number;
}

/**
 * Filter completion items against a prefix using fuzzy matching.
 * Returns items sorted by match score descending.
 */
export function filterCompletions(
  items: readonly CompletionItem[],
  prefix: string,
  options?: FilterOptions,
): CompletionItem[] {
  if (prefix.length === 0) {
    const max = options?.maxResults ?? 100;
    return items.slice(0, max) as CompletionItem[];
  }

  const scored: Array<{ item: CompletionItem; score: number }> = [];

  for (const item of items) {
    const filterText = item.filterText ?? item.label;
    const result = fuzzyMatch(filterText, prefix);
    if (result !== null) {
      scored.push({ item, score: result.score });
    }
  }

  scored.sort((a, b) => b.score - a.score);

  const max = options?.maxResults ?? 100;
  return scored.slice(0, max).map((s) => s.item);
}

/**
 * Check whether a completion item starts with the given prefix.
 */
export function prefixMatch(item: CompletionItem, prefix: string): boolean {
  const filterText = item.filterText ?? item.label;
  return filterText.toLowerCase().startsWith(prefix.toLowerCase());
}

/**
 * Fuzzy match a label against a pattern, returning a score and match positions.
 * Returns null if no match. Higher score = better match.
 * Consecutive character matches receive a bonus.
 */
export function fuzzyMatch(
  label: string,
  pattern: string,
): { score: number; matches: number[] } | null {
  if (pattern.length === 0) return { score: 0, matches: [] };
  if (pattern.length > label.length) return null;

  const labelLower = label.toLowerCase();
  const patternLower = pattern.toLowerCase();

  const matches: number[] = [];
  let score = 0;
  let patternIdx = 0;
  let lastMatchIdx = -1;
  let consecutiveBonus = 0;

  for (let i = 0; i < label.length && patternIdx < patternLower.length; i++) {
    if (labelLower[i] === patternLower[patternIdx]) {
      matches.push(i);

      // Exact case match bonus
      if (label[i] === pattern[patternIdx]) {
        score += 2;
      } else {
        score += 1;
      }

      // Consecutive character bonus
      if (lastMatchIdx === i - 1) {
        consecutiveBonus += 5;
        score += consecutiveBonus;
      } else {
        consecutiveBonus = 0;
      }

      // Word boundary bonus (match after separator)
      if (i === 0 || isWordBoundary(label, i)) {
        score += 10;
      }

      // Prefix bonus (matching from beginning)
      if (i === patternIdx) {
        score += 8;
      }

      lastMatchIdx = i;
      patternIdx++;
    }
  }

  if (patternIdx < patternLower.length) return null;

  // Penalty for gaps between matches
  const gapPenalty = (label.length - matches.length) * 0.5;
  score -= gapPenalty;

  return { score: Math.max(0, score), matches };
}

/**
 * Check whether label contains pattern as a contiguous substring.
 */
export function substringMatch(
  label: string,
  pattern: string,
): { score: number; matches: number[] } | null {
  const idx = label.toLowerCase().indexOf(pattern.toLowerCase());
  if (idx < 0) return null;

  const matches: number[] = [];
  for (let i = 0; i < pattern.length; i++) {
    matches.push(idx + i);
  }

  let score = pattern.length * 5;
  if (idx === 0) score += 20; // prefix bonus
  if (label[idx] === pattern[0]) score += 5; // case match

  return { score, matches };
}

/**
 * Match a pattern against PascalCase/camelCase abbreviations.
 * For example, "gIE" matches "getItemElement".
 */
export function camelCaseMatch(
  label: string,
  pattern: string,
): { score: number; matches: number[] } | null {
  if (pattern.length === 0) return { score: 0, matches: [] };

  const boundaries: number[] = [0];
  for (let i = 1; i < label.length; i++) {
    if (isUpperCase(label[i]!) && !isUpperCase(label[i - 1]!)) {
      boundaries.push(i);
    } else if (label[i] === '_' || label[i] === '-') {
      if (i + 1 < label.length) {
        boundaries.push(i + 1);
      }
    }
  }

  const matches: number[] = [];
  let patternIdx = 0;

  for (const boundaryIdx of boundaries) {
    if (patternIdx >= pattern.length) break;

    if ((label[boundaryIdx] ?? '').toLowerCase() === (pattern[patternIdx] ?? '').toLowerCase()) {
      matches.push(boundaryIdx);
      patternIdx++;

      // Try to match subsequent characters in the word segment
      let segEnd = boundaryIdx + 1;
      while (segEnd < label.length && patternIdx < pattern.length) {
        const nextBound = boundaries.find((b) => b > boundaryIdx);
        if (nextBound !== undefined && segEnd >= nextBound) break;

        if (label[segEnd]!.toLowerCase() === pattern[patternIdx]!.toLowerCase()) {
          matches.push(segEnd);
          patternIdx++;
        }
        segEnd++;
      }
    }
  }

  if (patternIdx < pattern.length) return null;

  const score = matches.length * 10 + (matches[0] === 0 ? 20 : 0);
  return { score, matches };
}

// ═══ §3 COMPLETION SCORING — composite score computation ═══

export interface CompletionScoringContext {
  readonly prefix: string;
  readonly lineContent?: string;
  readonly recentSymbols?: readonly string[];
  readonly recentCompletions?: readonly string[];
  readonly importedSymbols?: readonly string[];
  readonly triggerKind?: string;
}

/**
 * Compute a composite score for a completion item considering
 * match quality, kind priority, recency, and usage frequency.
 */
export function scoreCompletion(
  item: CompletionItem,
  context: CompletionScoringContext,
): number {
  const filterText = item.filterText ?? item.label;
  const matchResult = fuzzyMatch(filterText, context.prefix);
  const matchScore = matchResult !== null ? matchResult.score : 0;

  const kPriority = kindPriority(item.kind);
  const relevance = computeRelevance(item, context.recentSymbols ?? [], context.importedSymbols ?? []);

  let recencyScore = 0;
  if (context.recentCompletions) {
    const idx = context.recentCompletions.indexOf(item.label);
    if (idx >= 0) {
      recencyScore = Math.max(0, 100 - idx * 10);
    }
  }

  return (
    matchScore * COMPLETION_SORT_WEIGHTS.matchQuality +
    kPriority * COMPLETION_SORT_WEIGHTS.kindPriority +
    recencyScore * COMPLETION_SORT_WEIGHTS.recency +
    relevance * COMPLETION_SORT_WEIGHTS.frequency
  );
}

/**
 * Map a CompletionKind to a numeric priority.
 * Lower values = more commonly wanted items.
 */
export function kindPriority(kind: CompletionKind): number {
  const priorities: Record<CompletionKind, number> = {
    'variable': 100,
    'field': 95,
    'property': 90,
    'function': 85,
    'method': 85,
    'constructor': 80,
    'class': 75,
    'interface': 75,
    'struct': 75,
    'enum': 70,
    'enum-member': 70,
    'constant': 68,
    'module': 65,
    'type-parameter': 60,
    'keyword': 55,
    'snippet': 50,
    'text': 45,
    'value': 40,
    'unit': 35,
    'color': 30,
    'file': 25,
    'folder': 20,
    'reference': 15,
    'event': 10,
    'operator': 5,
  };
  return priorities[kind] ?? 0;
}

/**
 * Boost score for items that appear in nearby symbols or imports.
 */
export function computeRelevance(
  item: CompletionItem,
  symbols: readonly string[],
  imports: readonly string[],
): number {
  let score = 0;
  if (symbols.includes(item.label)) {
    score += 50;
  }
  if (imports.includes(item.label)) {
    score += 30;
  }
  if (item.preselect) {
    score += 25;
  }
  return score;
}

/** Weights for the composite completion score. */
export const COMPLETION_SORT_WEIGHTS = {
  matchQuality: 0.4,
  kindPriority: 0.2,
  recency: 0.2,
  frequency: 0.2,
} as const;

// ═══ §4 COMPLETION RESOLUTION — insert text, ranges, snippet expansion ═══

/**
 * Resolve the final insert text for a completion item, applying any insert text rules.
 */
export function resolveInsertText(item: CompletionItem): string {
  let text = item.insertText;
  if (item.insertTextRules) {
    if (item.insertTextRules.includes('keep-whitespace')) {
      // Preserve original whitespace — no modification needed
    }
    // 'insert-as-snippet' is handled by expandCompletionSnippet
  }
  return text;
}

/**
 * Compute the range of text that the completion will replace.
 */
export function computeReplaceRange(
  item: CompletionItem,
  position: Position,
  content: string,
): Range {
  if (item.range.start.line !== 0 || item.range.start.column !== 0 ||
      item.range.end.line !== 0 || item.range.end.column !== 0) {
    return item.range;
  }

  const lines = content.split('\n');
  const line = position.line < lines.length ? lines[position.line] : '';
  let wordStart = position.column;

  while (wordStart > 0 && isWordChar(line![wordStart - 1])) {
    wordStart--;
  }

  return {
    start: { line: position.line, column: wordStart },
    end: position,
  };
}

/**
 * Build the additional text edits that should be applied alongside the completion
 * (e.g. auto-imports).
 */
export function buildAdditionalEdits(item: CompletionItem): EditOperation[] {
  if (item.additionalTextEdits) {
    return [...item.additionalTextEdits];
  }
  return [];
}

/**
 * Expand a snippet insert text by substituting known variables.
 */
export function expandCompletionSnippet(
  insertText: string,
  variables: Record<string, string>,
): string {
  let result = insertText;

  // Replace ${VAR} and $VAR patterns
  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(new RegExp(`\\$\\{${key}\\}`, 'g'), value);
    result = result.replace(new RegExp(`\\$${key}(?![a-zA-Z0-9_])`, 'g'), value);
  }

  // Replace tab stops with empty strings for resolved text
  result = result.replace(/\$\{(\d+)(?::([^}]*))?\}/g, (_m, _idx, placeholder) => {
    return placeholder ?? '';
  });
  result = result.replace(/\$(\d+)/g, '');

  return result;
}

/**
 * Determine whether a completion should auto-complete based on trigger context.
 */
export function shouldAutoComplete(
  item: CompletionItem,
  triggerCharacter: string | undefined,
  config: { readonly autoCompleteOnTrigger?: boolean; readonly minPrefixLength?: number },
): boolean {
  if (item.preselect) return true;
  if (triggerCharacter !== undefined && (config.autoCompleteOnTrigger ?? true)) {
    if (item.commitCharacters?.includes(triggerCharacter)) return true;
  }
  const minLen = config.minPrefixLength ?? 1;
  const filterText = item.filterText ?? item.label;
  return filterText.length >= minLen;
}

/**
 * Check whether typing a character should commit (accept) the current completion.
 */
export function shouldCommitCharacter(item: CompletionItem, character: string): boolean {
  if (item.commitCharacters === undefined) return false;
  return item.commitCharacters.includes(character);
}

// ═══ §5 COMPLETION PROVIDERS — keyword, snippet, path, symbol, import ═══

/**
 * Produce keyword completion items for a given language.
 */
export function keywordCompletions(
  languageId: LanguageId,
  prefix: string,
): CompletionItem[] {
  const id = languageId as string;
  const keywords = KEYWORDS[id];
  if (keywords === undefined) return [];

  const items: CompletionItem[] = [];
  for (const kw of keywords) {
    if (prefix.length === 0 || kw.toLowerCase().startsWith(prefix.toLowerCase())) {
      items.push({
        id: asCompletionId(generateId('kw')),
        label: kw,
        kind: 'keyword',
        insertText: kw,
        range: { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
        sortText: `0_${kw}`,
      });
    }
  }

  return items;
}

/**
 * Produce snippet completion items matching a prefix.
 */
export function snippetCompletions(
  snippets: readonly SnippetDefinition[],
  prefix: string,
  language: LanguageId,
): CompletionItem[] {
  const items: CompletionItem[] = [];

  for (const snippet of snippets) {
    if (snippet.scope && !snippet.scope.includes(language)) continue;

    const matched = snippet.prefix.some((p) =>
      p.toLowerCase().startsWith(prefix.toLowerCase()),
    );
    if (!matched && prefix.length > 0) continue;

    items.push({
      id: asCompletionId(generateId('snip')),
      label: snippet.prefix[0] ?? snippet.name,
      kind: 'snippet',
      detail: snippet.description ?? snippet.name,
      documentation: snippet.body.join('\n'),
      insertText: snippet.body.join('\n'),
      insertTextRules: ['insert-as-snippet'],
      range: { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
      sortText: `2_${snippet.name}`,
    });
  }

  return items;
}

/**
 * Produce file/directory path completions from a partial path.
 */
export function pathCompletions(
  partialPath: string,
  availablePaths: readonly string[],
): CompletionItem[] {
  const items: CompletionItem[] = [];
  const lower = partialPath.toLowerCase();

  for (const p of availablePaths) {
    if (p.toLowerCase().startsWith(lower) || p.toLowerCase().includes(lower)) {
      const isDir = p.endsWith('/');
      items.push({
        id: asCompletionId(generateId('path')),
        label: p,
        kind: isDir ? 'folder' : 'file',
        insertText: p,
        range: { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
        sortText: `3_${p}`,
      });
    }
  }

  return items;
}

/**
 * Produce symbol-based completions from a document symbol list.
 */
export function symbolCompletions(
  symbols: readonly DocumentSymbol[],
  prefix: string,
): CompletionItem[] {
  const items: CompletionItem[] = [];

  function processSymbol(sym: DocumentSymbol): void {
    if (prefix.length === 0 || fuzzyMatch(sym.name, prefix) !== null) {
      items.push({
        id: asCompletionId(generateId('sym')),
        label: sym.name,
        kind: symbolKindToCompletionKind(sym.kind),
        detail: sym.detail,
        insertText: sym.name,
        range: { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
        sortText: `1_${sym.name}`,
      });
    }
    if (sym.children) {
      for (const child of sym.children) {
        processSymbol(child);
      }
    }
  }

  for (const sym of symbols) {
    processSymbol(sym);
  }

  return items;
}

/**
 * Produce import-based completions from available import paths and symbols.
 */
export function importCompletions(
  imports: readonly string[],
  prefix: string,
  _language: LanguageId,
): CompletionItem[] {
  const items: CompletionItem[] = [];

  for (const imp of imports) {
    if (prefix.length === 0 || imp.toLowerCase().startsWith(prefix.toLowerCase())) {
      items.push({
        id: asCompletionId(generateId('imp')),
        label: imp,
        kind: 'module',
        insertText: imp,
        range: { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
        sortText: `1_${imp}`,
      });
    }
  }

  return items;
}

// ═══ §6 KEYWORD LISTS — per-language keyword arrays ═══

/** Keyword lists for major languages. At least 30 keywords for each major language. */
export const KEYWORDS: Record<string, readonly string[]> = {
  typescript: [
    'abstract', 'any', 'as', 'asserts', 'async', 'await', 'bigint', 'boolean', 'break',
    'case', 'catch', 'class', 'const', 'constructor', 'continue', 'debugger', 'declare',
    'default', 'delete', 'do', 'else', 'enum', 'export', 'extends', 'false', 'finally',
    'for', 'from', 'function', 'get', 'if', 'implements', 'import', 'in', 'infer',
    'instanceof', 'interface', 'is', 'keyof', 'let', 'module', 'namespace', 'never',
    'new', 'null', 'number', 'object', 'of', 'override', 'package', 'private',
    'protected', 'public', 'readonly', 'require', 'return', 'satisfies', 'set',
    'static', 'string', 'super', 'switch', 'symbol', 'this', 'throw', 'true', 'try',
    'type', 'typeof', 'undefined', 'unique', 'unknown', 'var', 'void', 'while',
    'with', 'yield',
  ],
  javascript: [
    'abstract', 'arguments', 'async', 'await', 'boolean', 'break', 'byte', 'case',
    'catch', 'char', 'class', 'const', 'continue', 'debugger', 'default', 'delete',
    'do', 'double', 'else', 'enum', 'export', 'extends', 'false', 'final', 'finally',
    'float', 'for', 'from', 'function', 'get', 'goto', 'if', 'implements', 'import',
    'in', 'instanceof', 'int', 'interface', 'let', 'long', 'native', 'new', 'null',
    'of', 'package', 'private', 'protected', 'public', 'return', 'set', 'short',
    'static', 'super', 'switch', 'synchronized', 'this', 'throw', 'throws',
    'transient', 'true', 'try', 'typeof', 'undefined', 'var', 'void', 'volatile',
    'while', 'with', 'yield',
  ],
  python: [
    'False', 'None', 'True', 'and', 'as', 'assert', 'async', 'await', 'break',
    'class', 'continue', 'def', 'del', 'elif', 'else', 'except', 'finally', 'for',
    'from', 'global', 'if', 'import', 'in', 'is', 'lambda', 'match', 'case',
    'nonlocal', 'not', 'or', 'pass', 'raise', 'return', 'try', 'type', 'while',
    'with', 'yield',
  ],
  rust: [
    'as', 'async', 'await', 'break', 'const', 'continue', 'crate', 'dyn', 'else',
    'enum', 'extern', 'false', 'fn', 'for', 'if', 'impl', 'in', 'let', 'loop',
    'match', 'mod', 'move', 'mut', 'pub', 'ref', 'return', 'self', 'Self', 'static',
    'struct', 'super', 'trait', 'true', 'type', 'unsafe', 'use', 'where', 'while',
    'union', 'yield',
  ],
  go: [
    'break', 'case', 'chan', 'const', 'continue', 'default', 'defer', 'else',
    'fallthrough', 'for', 'func', 'go', 'goto', 'if', 'import', 'interface', 'map',
    'package', 'range', 'return', 'select', 'struct', 'switch', 'type', 'var',
    'bool', 'byte', 'complex64', 'complex128', 'error', 'float32', 'float64',
    'int', 'int8', 'int16', 'int32', 'int64', 'rune', 'string', 'uint', 'uint8',
    'uint16', 'uint32', 'uint64', 'uintptr', 'true', 'false', 'nil', 'iota',
    'append', 'cap', 'close', 'copy', 'delete', 'len', 'make', 'new', 'panic',
    'print', 'println', 'recover',
  ],
  java: [
    'abstract', 'assert', 'boolean', 'break', 'byte', 'case', 'catch', 'char',
    'class', 'const', 'continue', 'default', 'do', 'double', 'else', 'enum',
    'extends', 'false', 'final', 'finally', 'float', 'for', 'goto', 'if',
    'implements', 'import', 'instanceof', 'int', 'interface', 'long', 'native',
    'new', 'null', 'package', 'private', 'protected', 'public', 'record', 'return',
    'sealed', 'short', 'static', 'strictfp', 'super', 'switch', 'synchronized',
    'this', 'throw', 'throws', 'transient', 'true', 'try', 'var', 'void',
    'volatile', 'while', 'yield',
  ],
  css: [
    'align-content', 'align-items', 'align-self', 'animation', 'background',
    'border', 'border-radius', 'box-shadow', 'color', 'content', 'cursor',
    'display', 'flex', 'flex-direction', 'flex-wrap', 'font', 'font-family',
    'font-size', 'font-weight', 'gap', 'grid', 'height', 'justify-content',
    'left', 'line-height', 'margin', 'max-height', 'max-width', 'min-height',
    'min-width', 'opacity', 'outline', 'overflow', 'padding', 'position',
    'right', 'text-align', 'text-decoration', 'top', 'transform', 'transition',
    'width', 'z-index',
  ],
  html: [
    'a', 'abbr', 'article', 'aside', 'audio', 'b', 'body', 'br', 'button',
    'canvas', 'code', 'col', 'details', 'div', 'em', 'footer', 'form', 'h1',
    'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hr', 'html', 'i',
    'iframe', 'img', 'input', 'label', 'li', 'link', 'main', 'meta', 'nav',
    'ol', 'option', 'p', 'pre', 'script', 'section', 'select', 'span', 'strong',
    'style', 'summary', 'table', 'tbody', 'td', 'template', 'textarea', 'th',
    'thead', 'title', 'tr', 'ul', 'video',
  ],
  sql: [
    'ADD', 'ALL', 'ALTER', 'AND', 'AS', 'ASC', 'BETWEEN', 'BY', 'CASE',
    'CHECK', 'COLUMN', 'CONSTRAINT', 'CREATE', 'CROSS', 'DATABASE', 'DEFAULT',
    'DELETE', 'DESC', 'DISTINCT', 'DROP', 'ELSE', 'END', 'EXISTS', 'FOREIGN',
    'FROM', 'FULL', 'GROUP', 'HAVING', 'IF', 'IN', 'INDEX', 'INNER', 'INSERT',
    'INTO', 'IS', 'JOIN', 'KEY', 'LEFT', 'LIKE', 'LIMIT', 'NOT', 'NULL',
    'OFFSET', 'ON', 'OR', 'ORDER', 'OUTER', 'PRIMARY', 'REFERENCES', 'RIGHT',
    'SELECT', 'SET', 'TABLE', 'THEN', 'TRUNCATE', 'UNION', 'UNIQUE', 'UPDATE',
    'VALUES', 'VIEW', 'WHEN', 'WHERE', 'WITH',
  ],
  shell: [
    'alias', 'bg', 'break', 'case', 'cd', 'command', 'continue', 'declare',
    'do', 'done', 'echo', 'elif', 'else', 'esac', 'eval', 'exec', 'exit',
    'export', 'false', 'fi', 'for', 'function', 'getopts', 'if', 'in', 'let',
    'local', 'logout', 'read', 'readonly', 'return', 'select', 'set', 'shift',
    'source', 'test', 'then', 'time', 'trap', 'true', 'type', 'typeset',
    'ulimit', 'umask', 'unalias', 'unset', 'until', 'wait', 'while',
  ],
} as const;

// ═══ INTERNAL HELPERS ═══

function isWordBoundary(text: string, index: number): boolean {
  if (index === 0) return true;
  const prev = text[index - 1];
  const curr = text[index];
  if (prev === '_' || prev === '-' || prev === '.' || prev === '/') return true;
  if (isUpperCase(curr!) && !isUpperCase(prev!)) return true;
  return false;
}

function isUpperCase(ch: string): boolean {
  return ch >= 'A' && ch <= 'Z';
}

function isWordChar(ch: string | undefined): boolean {
  if (ch === undefined) return false;
  return /[a-zA-Z0-9_$]/.test(ch);
}

function symbolKindToCompletionKind(kind: string): CompletionKind {
  const map: Record<string, CompletionKind> = {
    'file': 'file',
    'module': 'module',
    'namespace': 'module',
    'package': 'module',
    'class': 'class',
    'method': 'method',
    'property': 'property',
    'field': 'field',
    'constructor': 'constructor' as CompletionKind,
    'enum': 'enum',
    'interface': 'interface',
    'function': 'function',
    'variable': 'variable',
    'constant': 'constant',
    'string': 'value',
    'number': 'value',
    'boolean': 'value',
    'array': 'value',
    'object': 'value',
    'key': 'property',
    'null': 'value',
    'enum-member': 'enum-member',
    'struct': 'struct',
    'event': 'event',
    'operator': 'operator',
    'type-parameter': 'type-parameter',
  };
  return (map[kind] ?? 'text') as CompletionKind;
}
